/**
 * Registrar countersignature — the assurance matrix a reader needs to weigh a
 * countersignature (`packages/core/src/verification/receipts/trust/registrar.ts`).
 *
 * This is a reference/methodology contract, not a per-receipt or per-tenant
 * one: the four holder classes and their five assurance properties are a
 * fixed table computed by `registrarAssuranceProfile`, identical for every
 * caller. The response is the ACTUAL output of that function (proxied through
 * the server, which is the only place the registrar module — it pulls in
 * `node:crypto` for signing — may run), never a UI paraphrase of it, so the
 * prose a reader sees can never drift from what the module itself asserts.
 *
 * `independent-of-acting-agent` is requested with no producer-boundary
 * evidence, which is the honest default for a reference view: it renders
 * `unknown`, never `yes`, for every holder class — see registrar.ts's own
 * comment on why that row is derived rather than a property of holder class.
 */

import { z } from "zod";
import type { Operation } from "../types";

export const VERIFICATION_REGISTRAR_SCHEMA_VERSION = 1 as const;

export const registrarHolderClass = z.enum([
  "internal-independent",
  "counterparty",
  "notary",
  "operated",
]);
export type RegistrarHolderClassDto = z.infer<typeof registrarHolderClass>;

export const registrarAssuranceSupport = z.enum(["yes", "no", "qualified", "unknown"]);
export type RegistrarAssuranceSupportDto = z.infer<typeof registrarAssuranceSupport>;

export const registrarAssuranceProperty = z.enum([
  "tamper-evident-offline",
  "independent-of-acting-agent",
  "independent-of-work-team",
  "independent-of-customer",
  "rollback-detectable-without-reader-pin",
]);
export type RegistrarAssurancePropertyDto = z.infer<typeof registrarAssuranceProperty>;

export const registrarAssuranceRowDto = z.object({
  property: registrarAssuranceProperty,
  question: z.string().min(1),
  support: registrarAssuranceSupport,
  note: z.string().min(1),
});
export type RegistrarAssuranceRowDto = z.infer<typeof registrarAssuranceRowDto>;

export const registrarAssuranceProfileDto = z.object({
  holderClass: registrarHolderClass,
  rows: z.array(registrarAssuranceRowDto).min(1),
});
export type RegistrarAssuranceProfileDto = z.infer<typeof registrarAssuranceProfileDto>;

export const verificationRegistrarAssuranceMatrixResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_REGISTRAR_SCHEMA_VERSION),
  /** `REGISTRAR_ATTESTATION_LIMITS` — what a countersignature does NOT claim. */
  limits: z.string().min(1),
  holderClasses: z.array(registrarHolderClass).min(1),
  /** One profile per holder class, in `holderClasses` order. */
  profiles: z.array(registrarAssuranceProfileDto).min(1),
});
export type VerificationRegistrarAssuranceMatrixResponse = z.infer<
  typeof verificationRegistrarAssuranceMatrixResponse
>;

const assuranceMatrixOp: Operation<
  void,
  void,
  void,
  VerificationRegistrarAssuranceMatrixResponse
> = {
  method: "GET",
  path: () => "/api/verification/registrar/assurance-matrix",
  response: verificationRegistrarAssuranceMatrixResponse,
};

export const verificationRegistrar = {
  assuranceMatrix: assuranceMatrixOp,
};
